/**
 * Unified Component Registry for Custom CRUD Components.
 *
 * Domains register their custom Tab, Block, and Field components here.
 * This enables SSOT (Single Source of Truth) - define once, use everywhere.
 *
 * Component Types:
 * - 'tab': Full tab replacement (entire tab content is custom)
 * - 'block': Block replacement (single block within a tab)
 * - 'field': Field replacement (single input field)
 *
 * Naming Convention: `{Domain}::{ComponentName}`
 * Examples: 'Workforce::SchedulingGrid', 'Communication::MessageComposer'
 *
 * @example
 * // In Domain portal.ts (SSOT registration):
 * import { registerBlockComponent, registerTabComponent } from '@ja/crud/services/componentRegistry';
 *
 * registerTabComponent(
 *   'Workforce::SchedulingGrid',
 *   () => import('./components/SchedulingGridTab.vue'),
 *   'workforce'
 * );
 *
 * registerBlockComponent(
 *   'Workforce::AvailabilityCalendar',
 *   () => import('./components/AvailabilityCalendarBlock.vue'),
 *   'workforce'
 * );
 */

import { shallowReactive, type AsyncComponentLoader, type Component } from 'vue';

// ============================================================================
// Types
// ============================================================================

export type ComponentType = 'tab' | 'block' | 'field';

export type ComponentModule = { default: Component };
export type AsyncComponent = AsyncComponentLoader<Component>;
export type ComponentEntry = ComponentModule | AsyncComponent;

interface ComponentRegistration {
    /** The Vue component or async loader */
    component: ComponentEntry;
    /** Package/domain that registered this */
    package: string;
    /** Component type */
    type: ComponentType;
}

// ============================================================================
// Registry State
// ============================================================================

/** Map structure: type -> name -> registration */
const registry = new Map<ComponentType, Map<string, ComponentRegistration>>();

// Initialize type maps with shallowReactive to enable Vue reactivity
registry.set('tab', shallowReactive(new Map()));
registry.set('block', shallowReactive(new Map()));
registry.set('field', shallowReactive(new Map()));

// ============================================================================
// Core API
// ============================================================================

/**
 * Register a custom component.
 *
 * @param type - Component type: 'tab', 'block', or 'field'
 * @param name - Unique identifier (e.g., 'Workforce::SchedulingGrid')
 * @param component - Vue component or async component loader
 * @param packageName - Domain/package name for debugging
 */
export function registerComponent(
    type: ComponentType,
    name: string,
    component: ComponentEntry,
    packageName: string = 'unknown',
): void {
    const typeMap = registry.get(type);
    if (!typeMap) {
        console.error(`[componentRegistry] Invalid component type: ${type}`);
        return;
    }

    if (typeMap.has(name)) {
        const existing = typeMap.get(name);
        console.warn(
            `[componentRegistry] ${type} '${name}' already registered by '${existing?.package}'. ` +
            `Overwriting with '${packageName}'.`,
        );
    }

    typeMap.set(name, {
        component,
        package: packageName,
        type,
    });

    if (import.meta.env.DEV) {
        console.log(`[componentRegistry] Registered ${type} '${name}' from '${packageName}'`);
    }
}

/**
 * Get a registered component.
 *
 * @param type - Component type: 'tab', 'block', or 'field'
 * @param name - Component identifier
 * @returns The component or undefined if not registered
 */
export function getComponent(type: ComponentType, name: string): ComponentEntry | undefined {
    const comp = registry.get(type)?.get(name)?.component;
    console.log(`[componentRegistry] getComponent(${type}, ${name}) ->`, comp ? 'FOUND' : 'MISSING');
    return comp;
}

/**
 * Check if a component is registered.
 *
 * @param type - Component type
 * @param name - Component identifier
 */
export function hasComponent(type: ComponentType, name: string): boolean {
    return registry.get(type)?.has(name) ?? false;
}

/**
 * Get registration info for a component.
 *
 * @param type - Component type
 * @param name - Component identifier
 */
export function getComponentInfo(type: ComponentType, name: string): ComponentRegistration | undefined {
    return registry.get(type)?.get(name);
}

/**
 * Get all registered component names for a type.
 *
 * @param type - Component type
 */
export function getRegisteredComponents(type: ComponentType): string[] {
    return Array.from(registry.get(type)?.keys() ?? []);
}

/**
 * Unregister a component (useful for testing).
 *
 * @param type - Component type
 * @param name - Component identifier
 */
export function unregisterComponent(type: ComponentType, name: string): boolean {
    return registry.get(type)?.delete(name) ?? false;
}

/**
 * Clear all registrations for a type (useful for testing).
 *
 * @param type - Component type
 */
export function clearRegistry(type: ComponentType): void {
    registry.get(type)?.clear();
}

/**
 * Clear all registrations (useful for testing).
 */
export function clearAllRegistries(): void {
    registry.get('tab')?.clear();
    registry.get('block')?.clear();
    registry.get('field')?.clear();
}

// ============================================================================
// Convenience Aliases
// ============================================================================

/**
 * Register a custom tab component.
 *
 * @example
 * registerTabComponent(
 *   'Workforce::SchedulingGrid',
 *   () => import('./components/SchedulingGridTab.vue'),
 *   'workforce'
 * );
 */
export function registerTabComponent(
    name: string,
    component: ComponentEntry,
    packageName: string = 'unknown',
): void {
    registerComponent('tab', name, component, packageName);
}

/**
 * Register a custom block component.
 *
 * @example
 * registerBlockComponent(
 *   'Workforce::AvailabilityCalendar',
 *   () => import('./components/AvailabilityCalendarBlock.vue'),
 *   'workforce'
 * );
 */
export function registerBlockComponent(
    name: string,
    component: ComponentEntry,
    packageName: string = 'unknown',
): void {
    registerComponent('block', name, component, packageName);
}

/**
 * Register a custom field component.
 *
 * @example
 * registerFieldComponent(
 *   'weeklyAvailability',
 *   () => import('./components/WeeklyAvailabilityField.vue'),
 *   'workforce'
 * );
 */
export function registerFieldComponent(
    name: string,
    component: ComponentEntry,
    packageName: string = 'unknown',
): void {
    registerComponent('field', name, component, packageName);
}

// ============================================================================
// Type-specific getters
// ============================================================================

export function getTabComponent(name: string): ComponentEntry | undefined {
    return getComponent('tab', name);
}

export function getBlockComponent(name: string): ComponentEntry | undefined {
    return getComponent('block', name);
}

export function getFieldComponent(name: string): ComponentEntry | undefined {
    return getComponent('field', name);
}

export function hasTabComponent(name: string): boolean {
    return hasComponent('tab', name);
}

export function hasBlockComponent(name: string): boolean {
    return hasComponent('block', name);
}

export function hasFieldComponent(name: string): boolean {
    return hasComponent('field', name);
}
